import { Images, Shield, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { RevealOnView } from "../reveal-on-view";

interface FeatureCardProps {
	icon: ReactNode;
	title: string;
	body: string;
}

function FeatureCard({ icon, title, body }: FeatureCardProps) {
	return (
		<article className="rounded-4xl border border-white/10 bg-white/6 p-7 shadow-[0_24px_80px_-48px_rgba(0,0,0,0.72)] backdrop-blur">
			<div className="mb-5 inline-flex size-12 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--color-app-accent),var(--color-app-accent-secondary))] text-white shadow-[0_18px_40px_-24px_rgba(255,90,95,0.8)]">
				{icon}
			</div>
			<h3 className="text-xl font-semibold tracking-[-0.03em] text-white">{title}</h3>
			<p className="mt-3 text-sm leading-7 text-white/68">{body}</p>
		</article>
	);
}

export function ValueSection() {
	const { t } = useTranslation("marketing");

	const featureCards = [
		{ icon: <Images className="size-5" />, title: t("feature_album_title"), body: t("feature_album_body") },
		{ icon: <Sparkles className="size-5" />, title: t("feature_story_title"), body: t("feature_story_body") },
		{ icon: <Shield className="size-5" />, title: t("feature_control_title"), body: t("feature_control_body") },
	];

	return (
		<section id="value" className="scroll-mt-28 py-24">
			<RevealOnView from="up">
				<div className="max-w-3xl">
					<p className="text-sm font-semibold uppercase tracking-[0.24em] text-app-accent-secondary">{t("section_value_eyebrow")}</p>
					<h2 className="mt-4 text-4xl font-semibold tracking-[-0.06em] text-white sm:text-5xl">{t("section_value_title")}</h2>
					<p className="mt-5 max-w-2xl text-lg leading-8 text-white/68">{t("section_value_body")}</p>
				</div>
			</RevealOnView>
			<div className="mt-12 grid gap-5 lg:grid-cols-3">
				{featureCards.map((feature, index) => (
					<RevealOnView key={feature.title} delayMs={index * 55} from="up">
						<FeatureCard icon={feature.icon} title={feature.title} body={feature.body} />
					</RevealOnView>
				))}
			</div>
		</section>
	);
}
